<template>
  <div class="text-center">
    <v-dialog v-model="isOpenDialog" :fullscreen="$vuetify.breakpoint.smAndDown" max-width="900" scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          Nhập EXCEL <v-spacer />
          <v-icon dark @click="cancel">close</v-icon>
        </v-card-title>
        <v-card-text class="pt-5">
          <!-- Bước chọn file -->
          <div
            v-if="step === 'import'"
            class="d-flex flex-column justify-center align-center mx-auto wrap"
            style="min-height: 280px"
          >
            <v-icon size="64" color="primary">mdi-file-excel</v-icon>
            <div class="mt-4">Tải tệp Excel (.xlsx) — các cột: "Mã số", "Đăng ký", "SL đăng ký"</div>
            <div class="mt-1 text--secondary caption">
              Nhập đăng ký cho: ngày <b>{{ date | ddmmyyyy }}</b> — bữa <b>{{ slotTitle || '—' }}</b>
            </div>
            <div class="mt-5">
              <v-btn color="primary" class="mr-4" outlined @click="downloadTemplate">Tải mẫu nhập</v-btn>
              <v-btn color="primary" depressed :loading="isSelecting" @click="onExcelUpload">
                Chọn tệp
                <input
                  ref="uploader"
                  type="file"
                  class="d-none"
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  @change="onFileChanged"
                />
              </v-btn>
            </div>
          </div>

          <!-- Bước preview -->
          <div v-else class="wrap pa-3">
            <div class="mb-2 font-weight-medium">Tệp: {{ fileName }} — {{ payload.length }} dòng đăng ký</div>
            <v-data-table :headers="previewHeaders" :items="rows" :items-per-page="10" dense>
              <template v-slot:[`item.stt`]="{ index }">{{ index + 1 }}</template>
            </v-data-table>
          </div>
        </v-card-text>
        <hr color="#E0E0E0" />
        <v-card-actions>
          <v-btn class="ma-2" width="100px" outlined @click="cancel">Hủy</v-btn>
          <v-spacer />
          <v-btn
            class="ma-2"
            width="100px"
            dark
            depressed
            color="#0D47A1"
            :disabled="!payload.length"
            :loading="saving"
            @click="save"
          >
            Lưu
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import readXlsxFile from 'read-excel-file'
import { get } from 'lodash'
import utils from '@/plugins/utils'
import { StudentMenu } from '@/plugins/api'

const schema = {
  'Mã số': { prop: 'code', type: String },
  'Đăng ký': { prop: 'sel', type: String },
  'SL đăng ký': { prop: 'amount', type: Number }
}
// Nhãn cột "Đăng ký" (file Xuất Excel) -> giá trị selectedMenu
const SELECTED_MENU_LABEL = { 'Thực đơn 1': 'menu1', 'Thực đơn 2': 'menu2', 'Không đăng ký': '0' }

export default {
  props: {
    date: { type: String, default: '' },
    mealSlot: { type: [String, Number], default: null },
    slotTitle: { type: String, default: '' }
  },
  data() {
    return {
      isOpenDialog: false,
      isSelecting: false,
      saving: false,
      step: 'import',
      fileName: '',
      rows: [],
      previewHeaders: [
        { text: 'STT', value: 'stt', sortable: false },
        { text: 'Mã số', value: 'code', sortable: false },
        { text: 'Đăng ký', value: 'sel', sortable: false },
        { text: 'SL đăng ký', value: 'amount', sortable: false }
      ]
    }
  },
  computed: {
    // các dòng hợp lệ sẽ gửi đi (bỏ "Chưa đăng ký"/nhãn lạ)
    payload() {
      const out = []
      this.rows.forEach(r => {
        const selectedMenu = SELECTED_MENU_LABEL[String(get(r, 'sel', '')).trim()]
        if (!selectedMenu) return
        out.push({ code: String(get(r, 'code', '')).trim(), selectedMenu, amount: r.amount })
      })
      return out
    }
  },
  methods: {
    openDialog() {
      this.resetInput()
      this.isOpenDialog = true
    },
    cancel() {
      this.isOpenDialog = false
      this.resetInput()
    },
    resetInput() {
      this.step = 'import'
      this.fileName = ''
      this.rows = []
    },
    onExcelUpload() {
      this.isSelecting = true
      window.addEventListener('focus', () => { this.isSelecting = false }, { once: true })
      this.$refs.uploader.click()
    },
    downloadTemplate() {
      // mẫu động đúng định dạng import (khớp cột file Xuất Excel)
      const headers = [
        { text: 'Mã số', value: 'code' },
        { text: 'Đăng ký', value: 'sel' },
        { text: 'SL đăng ký', value: 'amount' }
      ]
      const sample = [
        { code: '60123', sel: 'Thực đơn 1', amount: 1 },
        { code: '60124', sel: 'Thực đơn 2', amount: 1 },
        { code: '60125', sel: 'Không đăng ký', amount: '' }
      ]
      utils.exportExcel(sample, headers, 'Mau_nhap_dang_ky_suat_an')
    },
    async onFileChanged(e) {
      const file = e.target.files[0]
      if (!file) return
      this.$loading.active = true
      try {
        const result = await readXlsxFile(file, { schema })
        if (result.errors && result.errors.length) {
          const err = result.errors[0]
          this.$alert.error(`Lỗi đọc file - Dòng ${err.row}, cột ${err.column}`)
          return
        }
        this.rows = (result.rows || []).filter(r => r.code)
        this.fileName = file.name
        this.step = 'preview'
      } catch (err) {
        this.$alert.error({ title: 'File không đúng định dạng', description: 'Cần .xlsx theo mẫu' })
      } finally {
        this.$loading.active = false
        this.$refs.uploader.value = ''
      }
    },
    async save() {
      if (!this.date || !this.mealSlot) {
        this.$alert.error('Hãy chọn Bữa ăn và bấm Tìm kiếm trước khi nhập')
        return
      }
      if (!this.payload.length) {
        this.$alert.warn('Không có dòng đăng ký hợp lệ trong file')
        return
      }
      this.saving = true
      this.$loading.active = true
      try {
        const res = await StudentMenu.importRegister({
          date: this.date,
          mealSlot: this.mealSlot,
          rows: this.payload
        })
        this.$alert.success(`Nhập Excel thành công ${get(res, 'total', this.payload.length)} đăng ký`)
        this.isOpenDialog = false
        this.resetInput()
        this.$emit('imported')
      } catch (e) {
        // axios interceptor đã throw sẵn `message` (string hoặc { title, description }) -> báo thẳng
        this.$alert.error(e || 'Nhập Excel thất bại')
      } finally {
        this.saving = false
        this.$loading.active = false
      }
    }
  }
}
</script>

<style scoped>
.wrap {
  border: 1px solid rgb(222, 222, 222);
  border-radius: 4px;
}
</style>
